import './UserAvatar.css'

/**
 * Generate user initials (max 2 chars).
 */
function getInitials(user) {
  if (!user) return '?'
  if (user.name) {
    return user.name
      .split(' ')
      .map(n => n[0])
      .filter(Boolean)
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }
  return user.email?.[0]?.toUpperCase() || '?'
}

/**
 * Stable hashed color for a user (based on email/name).
 * Mirrors the algorithm used in Header.jsx so the chat avatar matches
 * the top-bar one.
 */
function getColor(user) {
  const str = user?.email || user?.name || ''
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  const hue = ((hash % 360) + 360) % 360
  return `hsl(${hue}, 45%, 45%)`
}

/**
 * UserAvatar — renders the user's avatar based on their chosen avatarMode.
 *
 * Props:
 *   user      — { name, email, imageUrl } shape (from useAuth())
 *   mode      — 'initials' | 'image' | 'ghibli'  (default 'initials')
 *   size      — pixel size; default 28
 *
 * `ghibli` is reserved for a future configurable Studio Ghibli-style
 * avatar set. For now it falls back to a placeholder icon so the
 * setting is wireable end-to-end.
 */
export default function UserAvatar({ user, mode = 'initials', size = 28 }) {
  const dimension = { width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.42)) }

  if (mode === 'image' && user?.imageUrl) {
    return (
      <div className="user-avatar-img" style={{ width: size, height: size }}>
        <img src={user.imageUrl} alt={user?.name || 'You'} />
      </div>
    )
  }

  if (mode === 'ghibli') {
    // Placeholder until the configurable Ghibli set lands.
    return (
      <div className="user-avatar-ghibli" style={{ width: size, height: size }} title="Ghibli avatar (placeholder)">
        <span role="img" aria-label="ghibli-avatar">🌿</span>
      </div>
    )
  }

  // Default — initials with hashed background colour.
  return (
    <div
      className="user-avatar-initials"
      style={{ ...dimension, backgroundColor: getColor(user) }}
      title={user?.name || user?.email || 'You'}
    >
      {getInitials(user)}
    </div>
  )
}
